import { StaticIApiTaskTypes, loadAllTaskTypes } from "@src/data-hooks/task-types/useTaskTypes";
import { CompanyTaskTypeData } from "@src/to-be-removed/LocalData/Company/CompanyTaskTypeData";
import { Tag } from "data-hooks/company/tags/tags.type";
import { DocumentReference, doc, getFirestore } from "firebase/firestore";
import { translate } from "libs/i18next/translations.util";
import { SelectInputData } from "../../../../../components/deprecated/SelectInputField/SelectInputField";
import { CompanyData } from "../../../Company/CompanyData";
import { CompanyInvoiceReceiverData } from "../../../Company/CompanyInvoiceReceiverData";
import { InvoicedToData } from "../../../Deviation/DeviationCostData";
import { DeviationData } from "../../../Deviation/DeviationData";
import { DeviationPriority } from "../../../Deviation/DeviationPriority";
import { DeviationStatus } from "../../../Deviation/DeviationStatus";
import { DeviationVisibility, DeviationVisibilityHelper } from "../../../Deviation/DeviationVisibility";
import { DataObjectType } from "../../DataObjects/DataObjectHelper";
import { LocalData } from "../../LocalData";
import { InputElementContext } from "../DataObjectField";
import { FirebaseDataSelectField, FirebaseDataSelectFieldOption } from "../Generic/FirebaseDataSelectField";

export class FirebaseDataDeviationField<FirebaseDataType extends DataObjectType> extends FirebaseDataSelectField<DeviationData, FirebaseDataType> {
  override encode = (value: DeviationData) => value.id;

  override convertValueToSelectInput(value: DeviationData): SelectInputData {
    return new SelectInputData(value.id, value.title);
  }

  getValueForKey(key: string): DeviationData | undefined {
    for (const property of LocalData.currentUser.properties) {
      const deviation = property.deviations.getData(key);
      if (deviation) return deviation;
    }
    return undefined;
  }

  getOptions(): FirebaseDataSelectFieldOption[] {
    return [];
  }

  getReadableStringRepresentation(value: DeviationData): string {
    return value.title;
  }

  override isValueValid = (value: any): boolean => {
    return value instanceof DeviationData;
  };
}

export class DeviationDataStatusField<FirebaseDataType extends DataObjectType> extends FirebaseDataSelectField<DeviationStatus, FirebaseDataType> {
  override hasCustomOptionOrder: boolean = true;

  override decodeStringAsRawValue = () => true;

  override encode = (value: DeviationStatus) => value.id;

  override convertValueToSelectInput(value: DeviationStatus): SelectInputData {
    return new SelectInputData(value.id + "", value.name);
  }

  getValueForKey(key: string): DeviationStatus | undefined {
    return !isNaN(parseInt(key)) ? DeviationStatus.getStatusWithId(parseInt(key)) : undefined;
  }

  getOptions(): FirebaseDataSelectFieldOption[] {
    return DeviationStatus.ALL.map((status) => {
      return {
        key: status.id + "",
        label: status.name,
      };
    });
  }

  getReadableStringRepresentation(value: DeviationStatus): string {
    return value.name;
  }

  getColor(value: DeviationStatus): string | undefined {
    return value ? value.color : undefined;
  }

  override isValueValid = (value: any): boolean => {
    return value instanceof DeviationStatus;
  };
}

export class DeviationDataPriorityField<FirebaseDataType extends DataObjectType> extends FirebaseDataSelectField<
  DeviationPriority,
  FirebaseDataType
> {
  override hasCustomOptionOrder: boolean = true;

  override decodeStringAsRawValue = () => true;

  override encode = (value: DeviationPriority) => value.id;

  override convertValueToSelectInput(value: DeviationPriority): SelectInputData {
    return new SelectInputData(value.id + "", value.name);
  }

  getValueForKey(key: string): DeviationPriority | undefined {
    return !isNaN(parseInt(key)) ? DeviationPriority.getPriorityWithId(parseInt(key)) : undefined;
  }

  getOptions(): FirebaseDataSelectFieldOption[] {
    return DeviationPriority.ALL.map((priority) => {
      return {
        key: priority.id + "",
        label: priority.name,
      };
    });
  }

  getReadableStringRepresentation(value: DeviationPriority): string {
    return value.name;
  }

  getColor(value: DeviationPriority): string | undefined {
    return value ? value.color : undefined;
  }

  equalComparator = (a: DeviationPriority | undefined, b: DeviationPriority | undefined) => {
    return a?.id === b?.id;
  };

  override isValueValid = (value: any) => {
    return value instanceof DeviationPriority;
  };
}

export class DataObjectTagField<FirebaseDataType extends DataObjectType> extends FirebaseDataSelectField<Tag, FirebaseDataType> {
  encode = (value: Tag) => value.id;

  override convertValueToSelectInput(value: Tag): SelectInputData {
    return new SelectInputData(value.id, this.getReadableStringRepresentation(value));
  }

  getValueForKey(key: string): Tag | undefined {
    return LocalData.getTagWithId(key);
  }

  getOptions(): FirebaseDataSelectFieldOption[] {
    return LocalData.staticTags.map((tag) => {
      return {
        key: tag.id,
        label: this.getReadableStringRepresentation(tag),
      };
    });
  }

  getReadableStringRepresentation(value: Tag): string {
    return `${value?.badgeIcon} ${translate(value?.name)}`;
  }

  override isValueValid = (value: any) => {
    return typeof value === "object" && value !== null && typeof value.id === "string";
  };
}
export class DataObjectRawTagField<FirebaseDataType extends DataObjectType> extends FirebaseDataSelectField<string, FirebaseDataType> {
  encode = (value: string) => value;

  override convertValueToSelectInput(value: string): SelectInputData {
    return new SelectInputData(value, this.getReadableStringRepresentation(value));
  }

  getValueForKey(key: string): string | undefined {
    return key;
  }

  getOptions(): FirebaseDataSelectFieldOption[] {
    return LocalData.staticTags.map((tag) => {
      return {
        key: tag.id,
        label: `${tag.badgeIcon} ${translate(tag.name)}`,
      };
    });
  }

  getReadableStringRepresentation(value: string): string {
    const statigTag = LocalData.staticTags.find((tag) => tag.id === value);
    if (statigTag) return `${statigTag.badgeIcon} ${translate(statigTag.name)}`;
    return value;
  }

  override isValueValid = (value: any) => {
    return typeof value === "string";
  };
}

type ExtendedDeviationDataType = "deviation" | "routine" | CompanyTaskTypeData;

export class DeviationDataTypeField<FirebaseDataType extends DataObjectType> extends FirebaseDataSelectField<
  ExtendedDeviationDataType,
  FirebaseDataType
> {
  override encode = (value: ExtendedDeviationDataType) => {
    if (value === "deviation" || value === "routine") return value;
    return doc(getFirestore(), value.getCompletePath());
  };
  override decode = (value: any) => {
    if (value === "deviation" || value === "routine") {
      return value;
    }
    if (value instanceof CompanyTaskTypeData) {
      return value;
    }
    if (value instanceof DocumentReference) {
      const taskTypeID = value.id;
      const companyID = value.parent.parent?.id;
      if (companyID) return LocalData.companies.addData(companyID).taskTypes.addData(taskTypeID);
    }
    return undefined;
  };

  override convertValueToSelectInput(value: ExtendedDeviationDataType): SelectInputData {
    return new SelectInputData(value, this.getReadableStringRepresentation(value));
  }

  getValueForKey(key: string): ExtendedDeviationDataType | undefined {
    if (["deviation", "routine"].includes(key)) return key as ExtendedDeviationDataType;
    return LocalData.companies.documents.flatMap((company) => company.taskTypes.documents).find((taskType) => taskType.id === key);
  }

  getOptions(): FirebaseDataSelectFieldOption[] {
    const options: FirebaseDataSelectFieldOption[] = [];
    LocalData.companies.documents.forEach((company) => {
      company.taskTypes.documents.forEach((taskType) => {
        if (taskType.title.translate() && !options.some((option) => option.key === taskType.id)) {
          options.push({ key: taskType.id, label: taskType.title.translate() });
        }
      });
    });
    options.sort((a, b) => a.label.localeCompare(b.label));
    return options;
  }

  getReadableStringRepresentation(value: ExtendedDeviationDataType): string {
    const key = typeof value === "string" ? value : value.id;
    return this.getOptions().find((option) => option.key === key)?.label ?? "";
  }

  override isValueValid = (value: any): boolean => {
    return (typeof value === "string" && ["deviation", "routine"].includes(value)) || value instanceof CompanyTaskTypeData;
  };

  loadAllDatas(context?: InputElementContext | undefined): Promise<any> {
    return loadAllTaskTypes().then((taskTypes) => {
      for (const taskType of taskTypes) {
        const data = LocalData.companies.addData(taskType.companyID).taskTypes.addData(taskType.id);
        data.setup(taskType);
      }
    });
  }

  loadData(value: ExtendedDeviationDataType): Promise<any> {
    if (typeof value === "string") {
      return Promise.resolve();
    }

    const promises: Promise<any>[] = [];

    if (!value.loaded) {
      promises.push(value.load());
    }
    if (value.parent && !value.parent.loaded) {
      promises.push(value.parent.load());
    }
    return Promise.all(promises);
  }
}

type TaskTypeIDType = string;
export class DeviationDataTaskTypeIDField<IRawDeviationType extends DataObjectType> extends FirebaseDataSelectField<
  TaskTypeIDType,
  IRawDeviationType
> {
  override encode = (value: TaskTypeIDType) => {
    return value;
  };
  override decode = (value: any) => {
    if (typeof value === "string") {
      return value;
    }
    if (value instanceof CompanyTaskTypeData) {
      return value.id;
    }
    if (value instanceof DocumentReference) {
      return value.id;
    }
    return undefined;
  };

  override convertValueToSelectInput(value: TaskTypeIDType): SelectInputData {
    return new SelectInputData(value, this.getReadableStringRepresentation(value));
  }

  getValueForKey(key: string): TaskTypeIDType | undefined {
    return key;
  }

  getOptions(): FirebaseDataSelectFieldOption[] {
    const options: FirebaseDataSelectFieldOption[] = [];
    for (const type of StaticIApiTaskTypes.all) {
      options.push({ key: type.id, label: translate(type.title) });
    }
    options.sort((a, b) => a.label.localeCompare(b.label));
    return options;
  }

  getReadableStringRepresentation(value: TaskTypeIDType): string {
    return this.getOptions().find((option) => option.key === value)?.label ?? "";
  }

  override isValueValid = (value: any): boolean => {
    return typeof value === "string" || value instanceof CompanyTaskTypeData;
  };

  loadData(value: TaskTypeIDType): Promise<any> {
    if (StaticIApiTaskTypes.all.find((type) => type.id === value)) {
      return Promise.resolve();
    } else {
      return loadAllTaskTypes();
    }
  }

  loadAllDatas(context?: InputElementContext | undefined): Promise<any> {
    return loadAllTaskTypes();
  }
}

export class DeviationDataVisibilityField<FirebaseDataType extends DataObjectType> extends FirebaseDataSelectField<
  DeviationVisibility,
  FirebaseDataType
> {
  override hasCustomOptionOrder: boolean = true;

  override encode = (value: DeviationVisibility) => value;

  override equalComparator = (a: DeviationVisibility | undefined, b: DeviationVisibility | undefined) => {
    return (
      Boolean(a?.landlords) === Boolean(b?.landlords) &&
      Boolean(a?.tenants) === Boolean(b?.tenants) &&
      Boolean(a?.allTenants) === Boolean(b?.allTenants)
    );
  };

  override convertValueToSelectInput(value: DeviationVisibility): SelectInputData {
    return DeviationVisibilityHelper.getOptionForVisibility(value ?? DeviationVisibilityHelper.getStandard(undefined));
  }

  override getValueForKey(key: string): DeviationVisibility | undefined {
    return DeviationVisibilityHelper.getVisibilityForType(key);
  }

  override getOptions(): FirebaseDataSelectFieldOption[] {
    return DeviationVisibilityHelper.getOptions([]).map((visibility) => {
      return {
        key: visibility.value,
        label: visibility.label,
      };
    });
  }

  override getReadableStringRepresentation(value: DeviationVisibility): string {
    return DeviationVisibilityHelper.getLabelForVisbility([], value);
  }

  override isValueValid = (value: any) => {
    return typeof value === "object" && value !== null && (value satisfies DeviationVisibility);
  };
}

export class DeviationDataInvoicedToField<FirebaseDataType extends DataObjectType> extends FirebaseDataSelectField<InvoicedToData, FirebaseDataType> {
  override encode = (value: InvoicedToData) => {
    return { companyID: value.company.id, invoiceReceiverID: value.invoiceReceiver.id };
  };

  override decode = (value: any): InvoicedToData | undefined => {
    if (
      typeof value === "object" &&
      value !== null &&
      value.company instanceof CompanyData &&
      value.invoiceReceiver instanceof CompanyInvoiceReceiverData
    ) {
      return value;
    }
    if (typeof value === "object" && value !== null && typeof value.companyID === "string" && typeof value.invoiceReceiverID === "string") {
      const company = LocalData.companies.addData(value.companyID);
      const invoiceReceiver = company.invoiceReceivers.addData(value.invoiceReceiverID);
      return { company, invoiceReceiver };
    }
    return typeof value === "string" ? this.decodeString(value) : value !== undefined ? this.decodeString(value + "") : undefined;
  };

  override convertValueToSelectInput(value: InvoicedToData): SelectInputData {
    const id = this.decode(value)?.invoiceReceiver.id;
    return new SelectInputData(id + "", this.getReadableStringRepresentation(value));
  }

  override getValueForKey(key: string): InvoicedToData | undefined {
    for (const company of LocalData.companies.documents) {
      const receiver = company.invoiceReceivers.getData(key);
      if (receiver) {
        return { company, invoiceReceiver: receiver };
      }
    }
    return undefined;
  }

  override getOptions(context?: InputElementContext): FirebaseDataSelectFieldOption[] {
    const receivers: CompanyInvoiceReceiverData[] = [];
    const companies = LocalData.properties.getData(context?.propertyID)?.companies ?? LocalData.companies.documents;
    for (const company of companies) {
      receivers.push(...company.invoiceReceivers.documents);
    }
    return receivers.map((receiver) => {
      return {
        key: receiver.id,
        label: receiver.receiver,
      };
    });
  }

  override getReadableStringRepresentation(value: InvoicedToData): string {
    return this.decode(value)?.invoiceReceiver.receiver ?? "";
  }

  override loadAllDatas(context?: InputElementContext | undefined): Promise<any> {
    const companies = LocalData.properties.getData(context?.propertyID)?.companies ?? LocalData.companies.documents;
    const promises: Promise<any>[] = [];
    for (const company of companies) {
      if (!company.invoiceReceivers.loaded) {
        promises.push(company.invoiceReceivers.loadWithPromise());
      }
    }
    return Promise.all(promises);
  }

  override loadData(value: InvoicedToData): Promise<any> {
    const data = this.decode(value);
    if (data?.company.loaded && data.invoiceReceiver.loaded) return Promise.resolve();
    return Promise.all([data?.company.load(), data?.invoiceReceiver.load()]);
  }

  override equalComparator = (a: InvoicedToData | undefined, b: InvoicedToData | undefined) => {
    const valueA = this.decode(a);
    const valueB = this.decode(b);
    return valueA?.company?.id === valueB?.company?.id && valueA?.invoiceReceiver?.id === valueB?.invoiceReceiver?.id;
  };

  override isValueValid = (value: any): boolean => {
    return (
      (typeof value === "object" &&
        value !== null &&
        value.company instanceof CompanyData &&
        value.invoiceReceiver instanceof CompanyInvoiceReceiverData) ||
      (typeof value === "object" && value !== null && "companyID" in value && "invoiceReceiverID" in value)
    );
  };
}
